// @ts-nocheck
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, message, Steps } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { ContractDetails } from '../components/contract/types';
import { CardContainer } from '../components/contract/CardContainer';
import { SectionHeader } from '../components/contract/SectionHeader';
import { Title } from '../components/design-system/typography';
import { colors, spacing } from '../components/design-system/tokens';
import { 
  ContractPartiesForm,
  ContractPaymentForm,
  ContractShipmentsGoodsForm 
} from '../components/contract/forms';
import { Company } from '../components/common/CompanyAutocomplete';
import { CloneContractModal } from '../components/contract/CloneContractModal';
import { contractsApi } from '../services/api';

const { Step } = Steps;

const ContractCreate: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Company | null>(null);
  const [contractReference, setContractReference] = useState<string>('');
  const [contractReferenceOptions, setContractReferenceOptions] = useState<{value: string, year: string, label: string}[]>([]);
  const [selectedCropYear, setSelectedCropYear] = useState<string>('');
  const [shipmentsGoodsData, setShipmentsGoodsData] = useState<any[]>([]);
  const [cloneModalVisible, setCloneModalVisible] = useState(false);

  const steps = [
    {
      title: 'Contract & Parties',
      content: 'contract-parties'
    },
    {
      title: 'Terms & Commission',
      content: 'contract-payment'
    },
    {
      title: 'Shipments & Goods',
      content: 'contract-shipments-goods'
    }
  ];

  const handleNext = async () => {
    try {
      // Validate current step fields
      const stepFields = getStepFields(currentStep);
      await form.validateFields(stepFields);
      
      // Get current form values to ensure they're preserved
      const currentValues = form.getFieldsValue(true);
      console.log('Current form values before moving to next step:', currentValues);
      
      // Log for debugging
      console.log('Moving from step', currentStep, 'to step', currentStep + 1);
      
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Validation failed:', error);
      message.error('Please fill in all required fields');
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const getStepFields = (step: number): string[] => {
    switch (step) {
      case 0:
        return ['contractDate', 'supplier', 'seller', 'buyer'];
      case 1:
        return ['sellerTerms', 'buyerTerms', 'sellerBasis', 'buyerBasis'];
      case 2:
        return []; // Shipments & goods validation handled separately
      default:
        return [];
    }
  };

  const handleSupplierChange = async (supplier: Company) => {
    setSelectedSupplier(supplier);
    
    const supplierCode = supplier.shortCode || supplier.code || (supplier.name ? supplier.name.substring(0, 2).toUpperCase() : '');
    if (!supplierCode) {
      message.warning('Supplier has no code configured');
      return;
    }

    try {
      const response = await contractsApi.getNextReference(supplierCode);
      setContractReferenceOptions(response.options);
      const currentYearOption = response.options[1];
      if (currentYearOption) {
        setContractReference(currentYearOption.value);
        setSelectedCropYear(currentYearOption.year);
        form.setFieldsValue({ rimtradeRef: currentYearOption.value });
      }
    } catch (error) {
      console.error('Failed to get next reference:', error);
      message.error('Failed to get contract reference options');
    }
  };

  const handleContractReferenceChange = (referenceValue: string) => {
    const selectedOption = contractReferenceOptions.find(opt => opt.value === referenceValue);
    if (selectedOption) {
      setContractReference(referenceValue);
      setSelectedCropYear(selectedOption.year);
      form.setFieldsValue({ rimtradeRef: referenceValue });
    }
  };

  const handleSubmit = async (values: any) => {
    console.log('handleSubmit called with values:', values);
    console.log('Current step:', currentStep);
    
    // Only allow submission from the last step
    if (currentStep !== steps.length - 1) {
      console.warn('Form submitted from wrong step, ignoring');
      return;
    }
    
    // Validate that we have the required data
    if (!values.contractDate || !selectedSupplier || !values.seller || !values.buyer) {
      console.error('Missing required fields');
      message.error('Please complete all required fields in previous steps');
      setCurrentStep(0); // Go back to first step
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Use the shipments goods data from state
      const shipmentsGoods = shipmentsGoodsData;
      
      // Transform form values to contract structure for API
      const contractData = {
        supplier_code: selectedSupplier.shortCode || selectedSupplier.code,
        buyer_code: values.buyer.split(' - ')[0], // Extract code from "CODE - Name" format
        contract_date: new Date(values.contractDate).toISOString().split('T')[0],
        crop_year: selectedCropYear.slice(-2), // Get last 2 digits of year
        terms: [
          {
            type: 'PAYMENT' as const,
            description: `Seller Terms: ${values.sellerTerms || 'Standard'}`,
            value: values.sellerTerms
          },
          {
            type: 'PAYMENT' as const,
            description: `Buyer Terms: ${values.buyerTerms || 'Standard'}`,
            value: values.buyerTerms
          }
        ]
      };

      console.log('Creating contract with API data:', contractData);

      // Call API to create contract
      const response = await contractsApi.createContract(contractData);
      console.log('Contract created successfully:', response);
      
      message.success(`Contract ${response.reference} created successfully!`);

      // Navigate to the newly created contract
      navigate(`/contracts/${response.reference}`);
      
    } catch (error: any) {
      console.error('Failed to create contract:', error);
      message.error(error.response?.data?.error || 'Failed to create contract. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    console.log('Rendering step content for step:', currentStep);
    switch (currentStep) {
      case 0:
        return <ContractPartiesForm 
          onSupplierChange={handleSupplierChange} 
          contractReferenceOptions={contractReferenceOptions}
          selectedReference={contractReference}
          onReferenceChange={handleContractReferenceChange}
        />;
      case 1:
        return <ContractPaymentForm />;
      case 2:
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <ContractShipmentsGoodsForm 
              onDataChange={setShipmentsGoodsData}
              contractContext={{
                buyer: form.getFieldValue('buyer') ? {
                  id: form.getFieldValue('buyer'),
                  name: form.getFieldValue('buyer'),
                  code: form.getFieldValue('buyer')
                } : undefined,
                supplier: selectedSupplier ? {
                  id: selectedSupplier.id,
                  name: selectedSupplier.name,
                  code: selectedSupplier.shortCode || selectedSupplier.name
                } : undefined
              }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{
      background: colors.background.primary,
      minHeight: 'calc(100vh - 112px)', // Account for header height
      padding: spacing['2xl']
    }}>
      <div style={{ 
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Page Header */}
        <div style={{ marginBottom: spacing['2xl'] }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Title>Create New Contract</Title>
              <p style={{ 
                color: colors.text.secondary,
                fontSize: '16px',
                marginTop: spacing.sm
              }}>
                Fill in the contract details below. All required fields must be completed.
              </p>
            </div>
            <Button
              type="default"
              icon={<CopyOutlined />}
              onClick={() => setCloneModalVisible(true)}
              style={{ marginTop: '8px' }}
            >
              Clone from Existing
            </Button>
          </div>
        </div>

        {/* Progress Steps */}
        <CardContainer variant="primary" style={{ marginBottom: spacing['2xl'] }}>
          <Steps current={currentStep} style={{ padding: `${spacing.lg} 0` }}>
            {steps.map(item => (
              <Step key={item.title} title={item.title} />
            ))}
          </Steps>
        </CardContainer>

        {/* Form Content */}
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            console.log('Form onFinish triggered');
            handleSubmit(values);
          }}
          onFinishFailed={(errorInfo) => {
            console.log('Form validation failed:', errorInfo);
          }}
          // Prevent any form submission except through button click
          onSubmit={(e) => {
            console.log('Form onSubmit triggered');
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          initialValues={{
            contractDate: new Date().toLocaleDateString('en-US', {
              day: '2-digit',
              month: 'short', 
              year: 'numeric'
            }).replace(',', ''),
            seller: 'Rimtrade, LLC - 789 Business Ave, Los Angeles, CA, 90210, United States'
          }}
        >
          <CardContainer variant="secondary">
            <SectionHeader 
              title={steps[currentStep]?.title || 'Contract Creation'}
              showBottomBorder={true}
            />
            
            <div style={{ padding: `${spacing['2xl']} 0` }}>
              {renderStepContent()}
            </div>

            {/* Navigation Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: spacing['2xl'],
              borderTop: `1px solid ${colors.border.default}`
            }}>
              <div>
                {currentStep > 0 && (
                  <Button onClick={handlePrev} size="large">
                    Previous
                  </Button>
                )}
              </div>

              <div style={{ display: 'flex', gap: spacing.md }}>
                <Button 
                  onClick={() => navigate('/contracts')}
                  size="large"
                >
                  Cancel
                </Button>
                
                {currentStep < steps.length - 1 ? (
                  <Button 
                    type="primary"
                    onClick={handleNext}
                    size="large"
                  >
                    Next
                  </Button>
                ) : (
                  <Button 
                    type="primary"
                    loading={isSubmitting}
                    size="large"
                    onClick={async () => {
                      console.log('Create Contract button clicked');
                      try {
                        // Get all form values, not just validate
                        const values = form.getFieldsValue(true);
                        console.log('All form values:', values);
                        
                        // Validate all fields
                        await form.validateFields();
                        
                        handleSubmit(values);
                      } catch (error) {
                        console.error('Form validation failed:', error);
                        message.error('Please complete all required fields');
                      }
                    }}
                  >
                    Create Contract
                  </Button>
                )}
              </div>
            </div>
          </CardContainer>
        </Form>

        <CloneContractModal
          visible={cloneModalVisible}
          onCancel={() => setCloneModalVisible(false)}
          onCloneSuccess={(newContractRef) => {
            setCloneModalVisible(false);
            navigate(`/contracts/${newContractRef}`);
          }}
        />
      </div>
    </div>
  );
};

export default ContractCreate;